<script setup>
  import { ref, onMounted, watch, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { useRoute } from "vue-router";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useSlideTemplatesStore } from "@stores/slideTemplates";
  import { useQuestionListStore } from "@stores/survey/reports/questionList";
  import { useChartTemplatesStore } from "@stores/chartTemplates/chartTemplates";
  import { useSurveyStore } from "@stores/survey/survey";
  import { Button } from "@/components/ui/button";
  import { Loader2, Save, Check, Download } from "lucide-vue-next";
  import ReportSlideList from "./ReportSlideList.vue";
  import ReportSlideEditor from "./ReportSlideEditor.vue";
  import SlideTemplatePicker from "./SlideTemplatePicker.vue";

  const route = useRoute();
  const reportStore = useReportsV2Store();
  const templatesStore = useSlideTemplatesStore();
  const questionListStore = useQuestionListStore();
  const chartTemplatesStore = useChartTemplatesStore();

  const { currentReport, selectedSlide, generating, hasSlides, saving, hasUnsavedChanges } = storeToRefs(reportStore);
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const showTemplatePicker = ref(false);

  const instance = getCurrentInstance();
  const cable = instance?.proxy?.$cable;

  // Watch for status changes (e.g., when slides finish building)
  watch(
    () => currentReport.value?.status,
    async (newStatus, oldStatus) => {
      if (oldStatus === "building_slides" && newStatus === "ready") {
        // Slides finished building, refresh the report data
        await reportStore.fetchReport(route.params.reportToken);
      }
    },
  );

  onMounted(async () => {
    // First fetch the report to get the theme token
    await reportStore.fetchReport(route.params.reportToken);

    const themeToken = currentReport.value?.master_report?.token;

    // Then fetch templates filtered by theme (survey-scoped render context)
    await Promise.all([
      templatesStore.fetchTemplates(themeToken, null, surveyToken.value),
      questionListStore.fetchQuestions(),
      chartTemplatesStore.fetchTemplates(themeToken, surveyToken.value),
    ]);
  });

  function selectSlide(slideId) {
    reportStore.selectSlide(slideId);
  }

  async function handleAddSlide(templateId) {
    if (insertBelowSlideId.value) {
      await reportStore.addSlideAfter(templateId, insertBelowSlideId.value);
      insertBelowSlideId.value = null;
    } else {
      await reportStore.addSlide(templateId);
    }
    showTemplatePicker.value = false;
  }

  async function handleDeleteSlide(slideId) {
    await reportStore.removeSlide(slideId);
  }

  async function handleReorderSlides(positions) {
    await reportStore.reorderSlides(positions);
  }

  const insertBelowSlideId = ref(null);

  function handleInsertBelow(slideId) {
    insertBelowSlideId.value = slideId;
    showTemplatePicker.value = true;
  }

  async function handleDuplicateSlide(slideId) {
    await reportStore.duplicateSlide(slideId);
  }

  async function saveReport() {
    await reportStore.saveReport();
  }

  async function generateAndDownload() {
    await reportStore.generateReport(cable, instance);
    // Download will happen automatically after generation completes
  }
</script>

<template>
  <div class="h-full flex flex-col bg-background">
    <!-- Header -->
    <header class="border-b px-6 py-4 flex items-center justify-between">
      <div>
        <h1 class="heading-page">{{ currentReport?.name }}</h1>
        <p class="text-sm text-muted-foreground">{{ currentReport?.slides?.length || 0 }} slide(s)</p>
      </div>

      <div class="flex items-center gap-3">
        <Button
          :variant="hasUnsavedChanges ? 'default' : 'outline'"
          :disabled="saving || !hasUnsavedChanges"
          @click="saveReport">
          <Loader2
            v-if="saving"
            class="w-4 h-4 mr-2 animate-spin" />
          <Save
            v-else-if="hasUnsavedChanges"
            class="w-4 h-4 mr-2" />
          <Check
            v-else
            class="w-4 h-4 mr-2" />
          {{ saving ? "Saving..." : hasUnsavedChanges ? "Save" : "Saved" }}
        </Button>

        <Button
          :disabled="generating || !hasSlides"
          @click="generateAndDownload">
          <Loader2
            v-if="generating"
            class="w-4 h-4 mr-2 animate-spin" />
          <Download
            v-else
            class="w-4 h-4 mr-2" />
          {{ generating ? "Generating..." : "Download" }}
        </Button>
      </div>
    </header>

    <!-- Main content -->
    <div class="flex-1 flex overflow-hidden min-h-0">
      <!-- Sidebar: Liste des slides -->
      <aside class="w-64 border-r overflow-y-auto p-4 min-h-0">
        <ReportSlideList
          :slides="currentReport?.slides || []"
          :selected-id="selectedSlide?.id"
          @select="selectSlide"
          @delete="handleDeleteSlide"
          @add="showTemplatePicker = true"
          @reorder="handleReorderSlides"
          @insert-below="handleInsertBelow"
          @duplicate="handleDuplicateSlide" />
      </aside>

      <!-- Main: Editor -->
      <main class="flex-1 overflow-y-auto min-h-0">
        <ReportSlideEditor
          v-if="selectedSlide"
          :slide="selectedSlide"
          :template="templatesStore.findTemplateForSlide(selectedSlide)" />

        <div
          v-else
          class="h-full flex items-center justify-center text-muted-foreground">
          <div class="text-center">
            <p>No slide selected</p>
            <Button
              variant="link"
              class="mt-2"
              @click="showTemplatePicker = true">
              Add your first slide
            </Button>
          </div>
        </div>
      </main>
    </div>

    <!-- Template picker dialog -->
    <SlideTemplatePicker
      v-model:open="showTemplatePicker"
      @select="handleAddSlide" />
  </div>
</template>
